import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Shield, 
  TrendingUp,  
  Book, 
  Star,
  Users,
  Zap,
  CheckCircle,
  Menu,
  X,
  Play,
  Clock,
  Award,
  ArrowLeft,
  ChevronDown,
  Globe,
  Loader
} from 'lucide-react';
import { databases } from '@/lib/appwrite/config';
import { appwriteConfig } from '@/lib/appwrite/config';
import { Models, Query } from 'appwrite';

// Interfaces matching your exact collection schema
interface Article extends Models.Document {
  title: string;
  content: string;
  excerpt: string;
  categoryId: string;
  readTime: number;
  featured: boolean;
  published: boolean;
  scheduledPublishDate?: string;
  tags: string[];
  views: number;
  slug: string;
}

interface Category extends Models.Document {
  title: string;
  description: string;
  icon: string;
  color: string;
  order: number;
  published: boolean;
  articleCount: number;
  slug: string;
}

interface Language {
  code: string;
  name: string;
  native: string;
}

interface LanguageSwitcherProps {
  currentLanguage: string;
  onLanguageChange: (lang: string) => void;
}

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string | number;
  description: string;
}

// Custom Language Switcher Component
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ currentLanguage, onLanguageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const languages: Language[] = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'bn', name: 'Bengali', native: 'বাংলা' },
    { code: 'te', name: 'Telugu', native: 'తెలుగు' },
    { code: 'mr', name: 'Marathi', native: 'मराठी' },
    { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
    { code: 'ur', name: 'Urdu', native: 'اردو' },
    { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી' },
    { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
    { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
    { code: 'or', name: 'Odia', native: 'ଓଡ଼ିଆ' },
    { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
    { code: 'as', name: 'Assamese', native: 'অসমীয়া' },
    { code: 'ne', name: 'Nepali', native: 'नेपाली' },
    { code: 'sa', name: 'Sanskrit', native: 'संस्कृतम्' }
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors text-white"
      >
        <Globe className="w-4 h-4 text-gray-400" />
        <span className="text-sm">{currentLang.native}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-48 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => {
                onLanguageChange(language.code);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 hover:bg-gray-700 transition-colors text-sm ${
                currentLanguage === language.code 
                  ? 'bg-blue-900/50 text-blue-400' 
                  : 'text-gray-300'
              }`}
            >
              <div className="flex justify-between items-center">
                <span>{language.native}</span>
                <span className="text-xs text-gray-500">{language.name}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const BusiconnUserGuide: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [readingTime, setReadingTime] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  // Dynamic data states
  const [categories, setCategories] = useState<Category[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Translation function (simplified - in real app, you'd use a proper translation service)
  const translate = (text: string, targetLang: string): string => {
    if (targetLang === 'en') return text;
    
    // Basic translations for demo - in production, you'd use Google Translate API or similar
    const translations: Record<string, Record<string, string>> = {
      'hi': {
        'Welcome to Busiconn': 'बिजिकॉन में आपका स्वागत है',
        'Getting Started': 'शुरुआत करना',
        'Safety & Security': 'सुरक्षा और सुरक्षा',
        'Platform Features': 'प्लेटफॉर्म की विशेषताएं',
        'Seller Growth Tips': 'विक्रेता वृद्धि के सुझाव',
        'Search guides...': 'गाइड खोजें...',
        'Back to App': 'ऐप पर वापस जाएं'
      },
      'bn': {
        'Welcome to Busiconn': 'বুসিকনে স্বাগতম',
        'Getting Started': 'শুরু করা',
        'Safety & Security': 'নিরাপত্তা ও সুরক্ষা',
        'Platform Features': 'প্ল্যাটফর্মের বৈশিষ্ট্য',
        'Seller Growth Tips': 'বিক্রেতা বৃদ্ধির টিপস'
      }
    };
    
    return translations[targetLang]?.[text] || text;
  };

  // Fetch categories from Appwrite
  const fetchCategories = async (): Promise<void> => {
    setIsLoadingCategories(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.userGuideCategories,
        [
          Query.equal('published', [true]),
          Query.orderAsc('order')
        ]
      );
      const fetchedCategories = response.documents as Category[];
      setCategories(fetchedCategories);
      
      // Set first category as default if none selected
      if (fetchedCategories.length > 0 && !selectedCategory) {
        setSelectedCategory(fetchedCategories[0].$id);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Fetch articles from Appwrite
  const fetchArticles = async (): Promise<void> => {
    setIsLoadingArticles(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.userGuideArtice,
        [
          Query.equal('published', [true]),
          Query.orderDesc('$createdAt')
        ]
      );
      setArticles(response.documents as Article[]);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setError('Failed to load articles');
    } finally {
      setIsLoadingArticles(false);
    }
  };

  // Update article view count
  const incrementViewCount = async (articleId: string, currentViews: number): Promise<void> => {
    try {
      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.userGuideArtice,
        articleId,
        { views: currentViews + 1 }
      );
      
      // Update local state
      setArticles(prev => prev.map(article => 
        article.$id === articleId 
          ? { ...article, views: currentViews + 1 }
          : article
      ));
    } catch (error) {
      console.error('Error updating view count:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCategories();
    fetchArticles();
  }, []);

  // Get current category data
  const currentCategory = categories.find(cat => cat.$id === selectedCategory);

  // Get articles for current category
  const categoryArticles = articles.filter(article => article.categoryId === selectedCategory);

  // Filter articles based on search
  const filteredArticles = categoryArticles.filter(article => 
    searchQuery === '' || 
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate reading time for current category
  useEffect(() => {
    if (categoryArticles.length > 0) {
      const totalTime = categoryArticles.reduce((sum, article) => sum + article.readTime, 0);
      setReadingTime(totalTime);
    }
  }, [categoryArticles]);

  // Icon mapping for categories
  const getIconComponent = (iconName: string): React.ReactNode => {
    const iconMap: Record<string, React.ReactNode> = {
      'Zap': <Zap className="w-5 h-5" />,
      'Shield': <Shield className="w-5 h-5" />,
      'Star': <Star className="w-5 h-5" />,
      'TrendingUp': <TrendingUp className="w-5 h-5" />,
      'Book': <Book className="w-5 h-5" />,
      'Users': <Users className="w-5 h-5" />,
      'default': <Book className="w-5 h-5" />
    };
    return iconMap[iconName] || iconMap['default'];
  };

  const StatCard: React.FC<StatCardProps> = ({ icon, title, value, description }) => (
    <div className="bg-gray-800 rounded-xl p-4 shadow-xl border border-gray-700 hover:bg-gray-750 transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <div className="p-2 rounded-lg bg-blue-900/50 text-blue-400">
          {icon}
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-white">{value}</div>
          <div className="text-xs text-gray-400">{description}</div>
        </div>
      </div>
      <h3 className="font-medium text-sm text-gray-200">{title}</h3>
    </div>
  );

  // Loading state
  if (isLoadingCategories || isLoadingArticles) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading user guide...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <X className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error Loading Guide</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => {
              setError(null);
              fetchCategories();
              fetchArticles();
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (categories.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Book className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No Content Available</h2>
          <p className="text-gray-400">The user guide content is being prepared. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{translate('Busiconn', currentLanguage)}</h1>
                <p className="text-xs text-gray-400">User Guide</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={translate('Search guides...', currentLanguage)}
                  className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none w-64 text-white placeholder-gray-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              <div className="language-switcher">
                <LanguageSwitcher 
                  currentLanguage={currentLanguage}
                  onLanguageChange={setCurrentLanguage}
                />
              </div>
              
              <button
                onClick={() => window.history.back()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {translate('Back to App', currentLanguage)}
              </button>
            </div>

            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-700 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-gray-800 border-t border-gray-700 px-4 py-4">
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={translate('Search guides...', currentLanguage)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-white placeholder-gray-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="language-switcher">
                <LanguageSwitcher 
                  currentLanguage={currentLanguage}
                  onLanguageChange={setCurrentLanguage}
                />
              </div>
              <button
                onClick={() => window.history.back()}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {translate('Back to App', currentLanguage)}
              </button>
            </div>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            {translate('Welcome to', currentLanguage)} <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Busiconn</span>
          </h1>
          <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
            Your comprehensive guide to building, growing, and securing your business on India's most trusted B2B platform
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard 
              icon={<Users className="w-6 h-6" />}
              title="Categories"
              value={categories.length}
              description="guide sections"
            />
            <StatCard 
              icon={<Book className="w-6 h-6" />}
              title="Articles"
              value={articles.length}
              description="helpful guides"
            />
            <StatCard 
              icon={<CheckCircle className="w-6 h-6" />}
              title="Trust & Safety"
              value="Bank Level"
              description="security standards"
            />
            <StatCard 
              icon={<Award className="w-6 h-6" />}
              title="Support"
              value="24/7"
              description="dedicated help"
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-4 sticky top-24">
              <h3 className="text-lg font-semibold text-white mb-4">Categories</h3>
              <nav className="space-y-2">
                {categories.map(category => {
                  const categoryArticleCount = articles.filter(article => article.categoryId === category.$id).length;
                  return (
                    <button
                      key={category.$id}
                      onClick={() => setSelectedCategory(category.$id)}
                      className={`w-full flex items-center p-3 rounded-lg text-left transition-all ${
                        selectedCategory === category.$id
                          ? 'bg-blue-900/50 text-blue-400 border-l-4 border-blue-500'
                          : 'hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <div className={`p-2 rounded-lg mr-3 ${
                        selectedCategory === category.$id ? 'bg-blue-900/50' : 'bg-gray-700'
                      }`}>
                        {getIconComponent(category.icon)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{translate(category.title, currentLanguage)}</div>
                        <div className="text-xs text-gray-500">{categoryArticleCount} articles</div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              {currentCategory ? (
                <>
                  <div className="flex items-center mb-6">
                    <div className={`p-3 rounded-lg ${currentCategory.color} bg-opacity-20 mr-4`}>
                      {getIconComponent(currentCategory.icon)}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{translate(currentCategory.title, currentLanguage)}</h2>
                      <p className="text-gray-400">
                        {categoryArticles.length} articles • {readingTime} min total read time
                      </p>
                      <p className="text-sm text-gray-500 mt-1">{currentCategory.description}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {filteredArticles.length === 0 ? (
                      <div className="text-center py-8">
                        <Book className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-400 mb-2">
                          {searchQuery ? 'No articles found' : 'No articles available'}
                        </h3>
                        <p className="text-gray-500">
                          {searchQuery ? 'Try adjusting your search or explore other categories.' : 'Articles for this category are being prepared.'}
                        </p>
                      </div>
                    ) : (
                      filteredArticles.map(article => (
                        <div key={article.$id} className="border border-gray-700 rounded-lg p-4 hover:bg-gray-700/50 transition-all duration-300 group relative">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-white mb-2 flex items-center group-hover:text-blue-400 transition-colors">
                                {article.title}
                                {article.featured && (
                                  <Star className="w-4 h-4 text-yellow-500 ml-2" />
                                )}
                              </h3>
                              <p className="text-gray-400 mb-3">{article.excerpt}</p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                                <span className="flex items-center">
                                  <Clock className="w-4 h-4 mr-1" />
                                  {article.readTime} min read
                                </span>
                                <span className="flex items-center">
                                  <Book className="w-4 h-4 mr-1" />
                                  Guide
                                </span>
                                <span className="flex items-center">
                                  <Users className="w-4 h-4 mr-1" />
                                  {article.views} views
                                </span>
                              </div>
                              {article.tags && article.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {article.tags.slice(0, 3).map((tag, index) => (
                                    <span key={`${tag}-${index}`} className="px-2 py-1 bg-blue-900/30 text-blue-300 text-xs rounded">
                                      {tag}
                                    </span>
                                  ))}
                                  {article.tags.length > 3 && (
                                    <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded">
                                      +{article.tags.length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <button 
                              onClick={() => incrementViewCount(article.$id, article.views)}
                              className="ml-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center opacity-0 group-hover:opacity-100"
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Read
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Book className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-400 mb-2">Select a Category</h3>
                  <p className="text-gray-500">Choose a category from the sidebar to view articles.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusiconnUserGuide;